import React, { useEffect, useId, useRef, useState } from 'react';
import Modal from '../../components/Modal/Modal';
import Dropdown from '../../components/Dropdown/Dropdown';
import DatePicker from '../../components/DatePicker/DatePicker';
import TimePicker from '../../components/TimePicker/TimePicker';
import { createLead, getLeadCourses } from '../../api/leads';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { todayYmd } from '../../utils/datetime';
import { toInstant } from './leadUi';

const EMPTY = { firstName: '', lastName: '', phone: '', courseId: '', ownerId: '', notes: '', callDate: '', callTime: '10:00' };

/** A phone typed by staff counts as complete when it has 9 local digits (or a full +998 number). */
function phoneLooksComplete(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 9 || (digits.length >= 11 && digits.length <= 15);
}

/**
 * Add a lead by hand (a phone call, a walk-in). The person who adds it owns it unless another
 * owner is picked; a first call can be planned right away. Mounted only while open.
 *
 *   {open && <LeadFormModal assignees={staff} onClose={close} onSaved={(lead) => open(lead)} />}
 */
export default function LeadFormModal({ assignees = [], onClose, onSaved }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const uid = useId();
  const id = (name) => `${uid}-${name}`;
  const [form, setForm] = useState(EMPTY);
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null); // { field?, text }
  const alive = useRef(true);
  // Set on every mount: StrictMode mounts twice in dev, and a ref left false would drop the result.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // The business's courses, for "which course do they ask about". Optional: a login that cannot
  // read the course list simply does not get the field.
  useEffect(() => {
    let cancelled = false;
    getLeadCourses()
      .then((list) => { if (!cancelled) setCourses(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(EMPTY);
  const invalid = (field) => error?.field === field;

  const submit = async () => {
    if (!form.firstName.trim()) {
      setError({ field: 'firstName', text: t('leads.form.nameRequired') });
      return;
    }
    if (!phoneLooksComplete(form.phone)) {
      setError({ field: 'phone', text: t('leads.form.phoneInvalid') });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await createLead({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || undefined,
        phoneNumber: form.phone.trim(),
        courseId: form.courseId || undefined,
        assignedToId: form.ownerId || undefined,
        notes: form.notes.trim() || undefined,
        nextCallAt: toInstant(form.callDate, form.callTime) || undefined,
      });
      onSaved(res?.lead || null);
    } catch (err) {
      if (!alive.current) return;
      setError({ text: err?.code === 409 ? t('leads.form.duplicate') : errorText(err, t('leads.form.saveError')) });
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const ownerOptions = [
    { value: '', label: t('leads.form.ownerMe') },
    ...assignees.filter((a) => a.id !== user?.id).map((a) => ({ value: a.id, label: a.name })),
  ];
  const courseOptions = [
    { value: '', label: t('leads.form.courseNone') },
    ...courses.map((c) => ({ value: c.id, label: c.title })),
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={t('leads.form.title')}
      size="lg"
      closeOnBackdrop={!dirty}
      as="form"
      onSubmit={submit}
      formProps={{ noValidate: true }}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('leads.form.cancel')}</button>
          <button type="submit" className="ds-btn ds-btn-primary" disabled={saving}>{t('leads.form.save')}</button>
        </>
      )}
    >
      <div className="ui-form-grid">
        <div className="ui-field">
          <label className="ui-label" htmlFor={id('first')}>
            {t('leads.form.firstName')}<span className="ui-req" aria-hidden="true">*</span>
          </label>
          <input
            id={id('first')}
            type="text"
            data-autofocus
            maxLength={100}
            autoComplete="off"
            value={form.firstName}
            aria-invalid={invalid('firstName') || undefined}
            aria-describedby={invalid('firstName') ? id('error') : undefined}
            onChange={(e) => setField('firstName', e.target.value)}
          />
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor={id('last')}>{t('leads.form.lastName')}</label>
          <input id={id('last')} type="text" maxLength={100} autoComplete="off" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} />
        </div>
        <div className="ui-field">
          <label className="ui-label" htmlFor={id('phone')}>
            {t('leads.form.phone')}<span className="ui-req" aria-hidden="true">*</span>
          </label>
          <input
            id={id('phone')}
            type="tel"
            inputMode="tel"
            maxLength={32}
            autoComplete="off"
            placeholder={t('leads.form.phonePlaceholder')}
            value={form.phone}
            aria-invalid={invalid('phone') || undefined}
            aria-describedby={invalid('phone') ? id('error') : undefined}
            onChange={(e) => setField('phone', e.target.value)}
          />
        </div>
        {courses.length > 0 && (
          <div className="ui-field">
            <span className="ui-label" id={id('course-label')}>{t('leads.form.course')}</span>
            <Dropdown value={form.courseId} onChange={(v) => setField('courseId', v)} options={courseOptions} labelledBy={id('course-label')} />
          </div>
        )}
        <div className="ui-field">
          <span className="ui-label" id={id('owner-label')}>{t('leads.form.owner')}</span>
          <Dropdown value={form.ownerId} onChange={(v) => setField('ownerId', v)} options={ownerOptions} labelledBy={id('owner-label')} />
        </div>
        <div className="ui-field">
          <span className="ui-label" id={id('call-label')}>{t('leads.form.firstCall')}</span>
          <div className="ui-picker-pair">
            <DatePicker
              value={form.callDate}
              onChange={(v) => setField('callDate', v)}
              min={todayYmd()}
              aria-label={`${t('leads.form.firstCall')}: ${t('leads.form.date')}`}
            />
            <TimePicker
              value={form.callTime}
              onChange={(v) => setField('callTime', v || '10:00')}
              aria-label={`${t('leads.form.firstCall')}: ${t('leads.form.time')}`}
              disabled={!form.callDate}
            />
          </div>
        </div>
        <div className="ui-field is-wide">
          <label className="ui-label" htmlFor={id('notes')}>{t('leads.form.notes')}</label>
          <textarea
            id={id('notes')}
            rows={3}
            maxLength={2000}
            value={form.notes}
            placeholder={t('leads.form.notesPlaceholder')}
            onChange={(e) => setField('notes', e.target.value)}
          />
        </div>
        {error && <p id={id('error')} className="ui-error is-wide" role="alert">{error.text}</p>}
      </div>
    </Modal>
  );
}
